"use client";

import React, { ReactNode, memo, useLayoutEffect } from "react";

import { DEFAULT_MODAL_TITLE_ID, DEFAULT_MODAL_BODY_ID } from "constants/modalTemplate";

type ModalProps = {
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
  width?: string;
  height?: string;
  paddingHorizontal?: number;
  paddingVertical?: number;
  "aria-labelledby"?: string;
  "aria-describedby"?: string;
};

const stopPropagation = (e: React.MouseEvent<HTMLElement>) => e.stopPropagation();

const Modal = memo(
  ({
    isOpen,
    onClose,
    children,
    width = "w-[400px]",
    height = "h-fit",
    paddingHorizontal,
    paddingVertical,
    "aria-labelledby": ariaLabelledby = DEFAULT_MODAL_TITLE_ID,
    "aria-describedby": ariaDescribedby = DEFAULT_MODAL_BODY_ID,
  }: ModalProps) => {
    useLayoutEffect(() => {
      if (document?.body) {
        if (isOpen) {
          document.body.style.overflow = "hidden";
        } else {
          document.body.style.overflow = "auto";
        }
      }
      // cleanup
      return () => {
        document.body.style.overflow = "auto";
      };
    }, [isOpen]);

    if (!isOpen) return null;
    return (
      <div
        className="fixed left-0 top-0 z-50 flex h-full w-full items-center justify-center bg-black bg-opacity-25"
        onClick={onClose}
      >
        <div
          className={`${width} ${height} bg-white p-${paddingVertical || 0} ${
            paddingHorizontal ? `px-${paddingHorizontal}` : ""
          } rounded-8 absolute flex flex-col items-center rounded-lg`}
          onClick={stopPropagation}
        >
          <div
            role="dialog"
            className="min-w-full"
            aria-modal="true"
            aria-labelledby={ariaLabelledby}
            aria-describedby={ariaDescribedby}
          >
            {children}
          </div>
        </div>
      </div>
    );
  }
);

Modal.displayName = "Modal";

export default Modal;
